// src/components/WiFiProvisionModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, View, Flex, Text, Button, Loader } from "@aws-amplify/ui-react";
import SimpleDialog from "../common/SimpleDialog";
import TroubleHelp from "../common/TroubleHelp";
import { gql } from "../../lib/gqlClient";
import { normalizeDeviceIdInput } from '../../utils/deviceId';
import { useAuthenticator } from "@aws-amplify/ui-react";

// --- NEW: backend polling dependencies ---
import * as queries from "../../graphql/queries";

export default function WiFiProvisionModal({ open, onClose, deviceId }) {
  // UI state
  // phase: "waiting" (auto polling) | "manual" (timed out, manual checks) | "online" | "error"
  const [phase, setPhase] = useState("waiting");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const [lastTelemetryAt, setLastTelemetryAt] = useState(null);
  const [lastCheckedAt, setLastCheckedAt] = useState(null);
  const phaseRef = useRef("waiting");
    useEffect(() => {
    phaseRef.current = phase;
    }, [phase]);

  // Control knobs
  const POLL_MS = 10000;
  const AUTO_WINDOW_MS = 10 * 60 * 1000; // 10 minutes

  const canonicalId = useMemo(() => normalizeDeviceIdInput(deviceId), [deviceId]);

  // Keep a ref to avoid setting state after close/unmount
  const cancelledRef = useRef(false);

  const { authStatus } = useAuthenticator((ctx) => [ctx.authStatus]);

  // Single check (used by both auto polling and the manual button)
  async function checkOnce() {
    if (!canonicalId) return { online: false };

    setChecking(true);
    setError("");

    try {
      const data = await gql(queries.getDevice, { id: canonicalId });
      const d = data?.getDevice || null;
      const last = d?.lastTelemetryAt || null;

      // "Online" for setup purposes = lastTelemetryAt exists
      const online = !!last;

      if (!cancelledRef.current) {
        setLastTelemetryAt(last);
        if (online) setPhase("online");
      }

      return { online, lastTelemetryAt: last };
    } catch (e) {
      if (!cancelledRef.current) {
        setError(e?.message || "Failed to check device status.");
        setPhase("error");
      }
      return { online: false, error: e };
    } finally {
      if (!cancelledRef.current) {
        setChecking(false);
        setLastCheckedAt(Date.now());
      }
    }
  }

  // Auto polling lifecycle
  useEffect(() => {
    if (!open) return;

    if (authStatus !== "authenticated") return;

    cancelledRef.current = false;

    // reset state when opened
    setPhase("waiting");
    phaseRef.current = "waiting";
    setError("");
    setChecking(false);
    setLastTelemetryAt(null);

    let intervalId = null;
    let timeoutId = null;

    // Start with an immediate check so it can flip to online quickly
    (async () => {
      await checkOnce();
    })();

    // Periodic checks
    intervalId = setInterval(async () => {
      // stop auto polling if we already succeeded or switched to manual/error
      if (cancelledRef.current) return;
      if (phaseRef.current !== "waiting") return;
      await checkOnce();
    }, POLL_MS);

    // After 2 minutes, stop auto polling and switch to manual mode (unless online already)
    timeoutId = setTimeout(() => {
      if (cancelledRef.current) return;
      setPhase((p) => {
        const next = (p === "online" ? "online" : "manual");
        phaseRef.current = next;
        return next;
      });
    }, AUTO_WINDOW_MS);

    // Cleanup on close/unmount
    return () => {
      cancelledRef.current = true;
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
    // Important: depend on "open" and canonicalId only.
    // We intentionally do NOT depend on "phase" to avoid restarting timers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canonicalId, authStatus]);

  const header =
    phase === "online" ? (
      <Alert heading="Device is online ✅" variation="success">
        Setup complete.
      </Alert>
    ) : (
      <Flex direction="column" gap="0.5rem">
        <Alert heading="Device registered successfully! 🎉" variation="success" />
        <Alert heading="Step 2: Connect to Wi-Fi" variation="info">
          To bring your device online, follow the instructions below.
        </Alert>
      </Flex>
    );

  const listStyle = {
    listStyleType: "decimal",
    paddingLeft: "1.25rem", // ~ml-5
    margin: 0,
    color: "var(--amplify-colors-font-secondary)",
    fontSize: "0.875rem", // ~text-sm
  };
  const liStyle = { marginTop: "0.25rem" }; 

  return (
    <SimpleDialog
      open={open}
      onClose={onClose}
      header={header}
      title={phase === "online" ? "Setup complete" : "Step 2: Connect to Wi-Fi"}
      maxWidth="36rem"
    >
      <View>
        <Flex direction="column" gap="0.75rem">
          {phase !== "online" && (
            <ol style={listStyle}>
              <li style={liStyle}>Power on the device by inserting the batteries.</li>
              <li style={liStyle}>
                Open Wi-Fi settings on your phone or computer and connect to the
                network named <b>LD-{canonicalId}</b>.
              </li>
              <li style={liStyle}>
                When prompted, enter the Wi-Fi name and password you want the
                device to use.
              </li>
            </ol>
          )}

          {/* --- Single waiting message during the auto window --- */}
          {phase === "waiting" && (
            <Flex direction="column" alignItems="center" gap="0.4rem">
              <Flex alignItems="center" gap="0.4rem">
                <Loader size="small" />
                <Text fontSize="0.8rem" color="font.secondary">
                  Checking for device connection…
                </Text>
              </Flex>

              <Text fontSize="0.9rem" color="font.secondary">
                Please follow the instructions above to connect your device.
              </Text>
            </Flex>
          )}

          {/* --- After 2 minutes, show "not online" + tips + manual check --- */}
          {phase === "manual" && (
            <View marginTop="medium">
              <Flex direction="column" alignItems="center" gap="0.25rem">
                <Text fontSize="0.9rem" color="font.tertiary">
                  Device not online.
                  Follow the instructions above to connect your device.
                </Text>
                <Button
                  size="small"
                  isLoading={checking}
                  onClick={async () => {
                    await checkOnce();
                  }}
                >
                  Check again
                </Button>
              
                {lastCheckedAt && (
                  <Text fontSize="0.75rem" color="font.tertiary">
                    Last checked: {new Date(lastCheckedAt).toLocaleTimeString()}
                  </Text>
                )}
              </Flex>
            </View>
          )}

          {phase === "error" && (
            <Alert variation="error" heading="Couldn’t check status">
              {error}
            </Alert>
          )}

          {phase === "online" && lastTelemetryAt && (
            <View>
              <Text fontSize="0.9rem" color="font.primary">
                ✅ You&apos;re online. You can close this window.
              </Text>
              <Text fontSize="0.85rem" color="font.secondary">
                Last seen: {new Date(lastTelemetryAt).toLocaleString()}
              </Text>
            </View>
          )}

          {phase !== "online" && <TroubleHelp ssid={`LD-${canonicalId}`} />}

          {phase === "online" && (
            <Flex justifyContent="flex-end" gap="0.5rem" marginTop="0.75rem">
              <Button variation="primary" onClick={onClose}>
                Done
              </Button>
            </Flex>
          )}
        </Flex>
      </View>
    </SimpleDialog>
  );
}
